import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

export default function Admin() {
  const { idToken } = useAuth();
  const [tab, setTab] = useState<'users' | 'sources' | 'tax' | 'audit'>('users');
  return (
    <div className="container">
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row">
          {(['users', 'sources', 'tax', 'audit'] as const).map(t => (
            <button key={t} className={tab === t ? '' : 'ghost'} onClick={() => setTab(t)}>{t.toUpperCase()}</button>
          ))}
        </div>
      </div>
      {tab === 'users' && <UsersTab token={idToken!} />}
      {tab === 'sources' && <SourcesTab token={idToken!} />}
      {tab === 'tax' && <TaxTab token={idToken!} />}
      {tab === 'audit' && <AuditTab token={idToken!} />}
    </div>
  );
}

function UsersTab({ token }: { token: string }) {
  const [users, setUsers] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  async function load() { const r = await api('/api/admin/users', { token }); setUsers(r.users); }
  useEffect(() => { load(); }, []);

  async function update(sub: string, patch: any) {
    setBusy(true);
    try { await api('/api/admin/users', { method: 'PUT', token, body: JSON.stringify({ sub, ...patch }) }); await load(); }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <h3>Users {busy && <span className="muted small">saving…</span>}</h3>
      <table>
        <thead><tr><th>Email</th><th>Role</th><th>Approved</th><th>Zip</th><th>Radius</th><th>Extra zips</th><th>Actions</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.sub}>
              <td>{u.email}</td>
              <td>
                <select value={u.role} onChange={e => update(u.sub, { role: e.target.value })}>
                  <option value="user">user</option><option value="admin">admin</option>
                </select>
              </td>
              <td><span className={`pill ${u.approved ? 'good' : 'bad'}`}>{u.approved ? 'yes' : 'no'}</span></td>
              <td>{u.zip}</td>
              <td>
                <input type="number" defaultValue={u.radius} style={{ width: 70 }}
                  onBlur={e => update(u.sub, { radius: parseInt(e.target.value, 10) })} />
              </td>
              <td>
                <input defaultValue={u.extraZips.join(',')} style={{ width: 180 }} placeholder="comma-separated"
                  onBlur={e => update(u.sub, { extraZips: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              </td>
              <td>
                {!u.approved && <button onClick={() => update(u.sub, { approved: true })}>Approve</button>}
                {u.approved && <button className="ghost" onClick={() => update(u.sub, { approved: false })}>Revoke</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourcesTab({ token }: { token: string }) {
  const [sources, setSources] = useState<any[]>([]);
  async function load() { const r = await api('/api/admin/sources', { token }); setSources(r.sources); }
  useEffect(() => { load(); }, []);

  async function update(name: string, patch: any) {
    await api('/api/admin/sources', { method: 'PUT', token, body: JSON.stringify({ name, ...patch }) });
    await load();
  }

  return (
    <div className="card">
      <h3>Data sources</h3>
      <table>
        <thead><tr><th>Source</th><th>Enabled</th><th>TTL (min)</th><th>Description</th></tr></thead>
        <tbody>
          {sources.map(s => (
            <tr key={s.name}>
              <td><b>{s.label}</b><br/><span className="muted small">{s.name}</span></td>
              <td>
                <button className={s.enabled ? '' : 'ghost'} onClick={() => update(s.name, { enabled: !s.enabled, ttlMinutes: s.ttlMinutes })}>{s.enabled ? 'On' : 'Off'}</button>
              </td>
              <td>
                <input type="number" defaultValue={s.ttlMinutes} style={{ width: 100 }}
                  onBlur={e => update(s.name, { enabled: s.enabled, ttlMinutes: parseInt(e.target.value, 10) })} />
              </td>
              <td className="muted small">{s.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TaxTab({ token }: { token: string }) {
  const [rules, setRules] = useState<any[]>([]);
  useEffect(() => { api('/api/admin/tax-rules', { token }).then(r => setRules(r.rules)); }, []);
  return (
    <div className="card">
      <h3>Tax rules</h3>
      <p className="muted small">Editing here updates state + county overrides used by the breakdown engine. To bulk-load, edit <span className="kbd">seed/tax-rules.json</span> and re-run the seed script.</p>
      <table>
        <thead><tr><th>State</th><th>County</th><th>Fed cpg</th><th>State excise</th><th>Sales %</th><th>Districts</th></tr></thead>
        <tbody>
          {rules.map((r, i) => (
            <tr key={i}>
              <td>{r.state}</td><td className="muted">{r.county ?? '—'}</td>
              <td>${((r.fedExcise ?? 0) + (r.fedLust ?? 0)).toFixed(3)}</td>
              <td>${(r.stateExcise ?? 0).toFixed(3)}</td>
              <td>{((r.stateSalesRate ?? 0) * 100).toFixed(2)}%</td>
              <td className="muted small">{(r.districts ?? []).map((d: any) => d.name).join(', ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ token }: { token: string }) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => { api('/api/admin/audit', { token }).then(r => setRows(r.rows)); }, []);
  return (
    <div className="card">
      <h3>Audit log</h3>
      <table>
        <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Meta</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="muted small">{r.ts}</td>
              <td>{r.actor}</td>
              <td>{r.action}</td>
              <td className="muted">{r.target}</td>
              <td className="muted small"><code>{JSON.stringify(r.meta)}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
